import { DeepNullablePartial, ErrorCode } from '@approvalmax/types';
import { toast } from '@approvalmax/ui/src/components';
import { isApiError } from '@approvalmax/utils/src/helpers/error/isApiError';
import { useMutation } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { backend, domain, stateTree } from 'modules/data';
import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { amplitudeService } from 'services/amplitude';
import { notificationService } from 'services/notification';
import { routingService } from 'services/routing';
import { useInvalidateGetAmaxPayXeroRequestDetails, usePublishRequest } from 'shared/data/webApp/v1';
import { hasChangesState } from 'shared/states';
import { getPath, Path } from 'urlBuilder';

import { useEditRequestForm } from '../../../hooks/useEditRequestForm';
import { getEditCommands, validateRequest } from '../../../selectors/requestSelectors';
import { getXeroAmaxPayBatchPaymentRequestTransfer } from '../../../selectors/xero';
import { messages } from './XeroAmaxPayBatchPaymentForm.messages';
import {
    UsePrepopulateSelectedBankAccountFromLinesParams,
    UseSaveAndSubmitMutationParams,
} from './XeroAmaxPayBatchPaymentForm.types';

export const useSaveAndSubmitMutation = ({
    xeroAmaxPayBatchPayment,
    request,
    setBillInvoiceRequestIdsWithExceededAmount,
    setBillInvoiceRequestIdsWithError,
    setDeletedPaymentDetails,
    setPaymentDetailsDiscrepancies,
}: UseSaveAndSubmitMutationParams) => {
    const commands = useSelector(getEditCommands);
    const setHasChanges = useSetAtom(hasChangesState);

    const invalidateGetAmaxPayXeroRequestDetails = useInvalidateGetAmaxPayXeroRequestDetails();

    const { mutateAsync: publishRequest, isLoading: isLoadingEditRequest } = usePublishRequest();
    const { mutateAsync: editRequest, isLoading: isLoadingPublishRequest } = useEditRequestForm();

    const { mutate } = useMutation(async (transfer: DeepNullablePartial<backend.transfers.RequestEditTransfer>) => {
        setBillInvoiceRequestIdsWithExceededAmount([]);
        setBillInvoiceRequestIdsWithError([]);
        setHasChanges(false);

        const shouldPublish = !commands.saveAndSubmit.hidden && !commands.saveAndSubmit.disabled;

        try {
            await editRequest(
                {
                    body: transfer,
                },
                {
                    onSuccess: () => {
                        if (!shouldPublish) {
                            routingService.push(getPath(Path.request, request.id, request.companyId));
                        }
                    },
                }
            );

            if (shouldPublish) {
                await publishRequest(
                    {
                        body: {
                            requestId: request.id,
                            companyId: request.companyId,
                        },
                    },
                    {
                        onSuccess: () => {
                            amplitudeService.sendData('requests: create request', {
                                'request type': domain.IntegrationCode.XeroAmaxPayBatchPayment.toLocaleLowerCase(),
                            });
                            toast.success(messages.saveAndSubmitRequestSuccess);
                            routingService.push(getPath(Path.request, request.id, request.companyId));
                        },
                    }
                );
            }
        } catch (error) {
            if (isApiError(error)) {
                switch (error.code) {
                    case ErrorCode.E4158_XERO_BILL_BATCH_PAYMENT_EXCEEDED_AMOUNT: {
                        const billInvoiceRequestIdsWithExceededAmount = error.billInvoiceRequestIds || [];

                        setBillInvoiceRequestIdsWithExceededAmount(billInvoiceRequestIdsWithExceededAmount);
                        break;
                    }

                    case ErrorCode.E4159_XERO_BILL_BATCH_PAYMENT_DIFFERENT_CURRENCY:
                    case ErrorCode.E4161_XERO_BILL_BATCH_PAYMENT_INCORRECT_STATUS: {
                        const billInvoiceRequestIdsWithError = error.billInvoiceRequestIds || [];

                        setBillInvoiceRequestIdsWithError(billInvoiceRequestIdsWithError);
                        break;
                    }

                    case ErrorCode.E8302_PAYMENT_DETAILS_WERE_EDITED_BEFORE_PUBLISH:
                        if (error.paymentDetailsDiscrepancies) {
                            setPaymentDetailsDiscrepancies(error.paymentDetailsDiscrepancies);
                        }

                        break;

                    case ErrorCode.E8303_PAYMENT_DETAILS_WERE_DELETED_BEFORE_PUBLISH:
                        if (error.deletedPaymentDetails) {
                            setDeletedPaymentDetails(error.deletedPaymentDetails);
                        }

                        break;
                }
            }
        }
    });

    const saveAndSubmit = useCallback(
        (commentText?: string, newDetails?: domain.XeroAmaxPayBatchPayment) => {
            const details = {
                ...request.details,
                ...xeroAmaxPayBatchPayment,
                ...newDetails,
            };
            const state = window.ApprovalMax.app.getStore().getState() as stateTree.State;
            const requestWithBillBatchPayment = { ...request, details };
            const errors = validateRequest(state, requestWithBillBatchPayment);

            if (errors.length > 0) {
                errors.forEach((error) => {
                    notificationService.showErrorToast(error);
                });

                return;
            }

            const transfer = getXeroAmaxPayBatchPaymentRequestTransfer(state, requestWithBillBatchPayment, commentText);

            mutate(transfer, {
                onSuccess: () => {
                    invalidateGetAmaxPayXeroRequestDetails({
                        path: { companyId: request.companyId, requestId: request.id },
                    });
                },
            });
        },
        [request, xeroAmaxPayBatchPayment, mutate, invalidateGetAmaxPayXeroRequestDetails]
    );

    return { saveAndSubmit, isSaving: isLoadingEditRequest || isLoadingPublishRequest };
};

export const usePrepopulateSelectedBankAccountFromLines = ({
    selectedSimplifiedBankAccount,
    setSelectedSimplifiedBankAccount,
    bankAccountFromBill,
}: UsePrepopulateSelectedBankAccountFromLinesParams) =>
    useEffect(() => {
        if (!selectedSimplifiedBankAccount && bankAccountFromBill) {
            setSelectedSimplifiedBankAccount(bankAccountFromBill);
        }
    }, [bankAccountFromBill, selectedSimplifiedBankAccount, setSelectedSimplifiedBankAccount]);
